import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Info, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { InputField } from '../../components/ui/input-field';
import { SelectField } from '../../components/ui/select-field';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import AdminShell from '../../components/Survey/admin/AdminShell';
import { Actions as SURVEY_ACTIONS } from '../../store/actions/surveyActions';

const SurveyCreate = ({ loading, reportingPeriods, createCampaign, getReportingPeriods }) => {
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: 'FY 2025-26 WFH & Commuting Survey',
      reportingPeriod: '',
      deadlineDate: '2026-05-30',
    },
  });

  useEffect(() => {
    getReportingPeriods();
  }, [getReportingPeriods]);

  const onSubmit = async (values) => {
    const selectedOption = reportingPeriods.find((period) => period.value === values.reportingPeriod);
    const [startFromValue, endFromValue] = String(values.reportingPeriod || '').split('/');
    const reportingPeriodStart = selectedOption?.reportingPeriodStart || startFromValue || null;
    const reportingPeriodEnd = selectedOption?.reportingPeriodEnd || endFromValue || null;
    const payload = {
      name: values.name,
      reportingPeriodStart,
      reportingPeriodEnd,
      deadlineDate: values.deadlineDate || null,
    };

    const campaign = await createCampaign(payload);
    if (campaign?.id) history.push(`/surveys/${campaign.id}`);
  };

  const selectedPeriod = watch('reportingPeriod');
  const reportingPeriodOptions = reportingPeriods?.length ? reportingPeriods : [];

  useEffect(() => {
    if (!reportingPeriodOptions.length) return;
    const exists = reportingPeriodOptions.some((period) => period.value === selectedPeriod);
    if (!exists) {
      const currentPeriod = reportingPeriodOptions.find((period) => period.isCurrent);
      setValue('reportingPeriod', (currentPeriod || reportingPeriodOptions[0]).value, { shouldValidate: true });
    }
  }, [reportingPeriodOptions, selectedPeriod, setValue]);

  return (
    <AdminShell
      breadcrumbs={[
        { label: 'Surveys', to: '/surveys' },
        { label: 'New campaign' },
      ]}
      eyebrow='Step 1 of 3'
      title='Create a new survey campaign'
      description='Name the campaign and pick the reporting period. You will add facilities and invite employees in the next steps.'>
      <div className='grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8'>
        <Card>
          <CardHeader>
            <CardTitle>Campaign details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputField
                label='Campaign name'
                hint='Shown to employees in the invitation email. Use something they will recognise.'
                error={errors.name?.message}
                {...register('name', { required: 'Campaign name is required' })}
              />

              <SelectField
                label='Reporting period'
                value={selectedPeriod}
                onChange={(value) => setValue('reportingPeriod', value, { shouldValidate: true })}
                options={reportingPeriodOptions}
                hint='Used to select DEFRA emission factors and pro-rate joiners.'
                error={errors.reportingPeriod?.message}
                disabled={Boolean(loading?.reportingPeriods) || reportingPeriodOptions.length === 0}
                placeholder={loading?.reportingPeriods ? 'Loading periods...' : 'Select reporting period'}
              />
              <input type='hidden' {...register('reportingPeriod', { required: 'Reporting period is required' })} />

              <InputField
                label='Deadline (optional)'
                type='date'
                hint='We will auto-send reminder emails 7 and 2 days before this date.'
                error={errors.deadlineDate?.message}
                {...register('deadlineDate')}
              />

              <div className='flex items-center justify-end gap-2 mt-6 pt-6 border-t border-border'>
                <Button variant='outline' type='button' asChild>
                  <Link to='/surveys'>
                    <ArrowLeft size={14} strokeWidth={1.5} />
                    Cancel
                  </Link>
                </Button>
                <Button type='submit' loading={Boolean(loading?.createCampaign)}>
                  <Save size={14} strokeWidth={1.5} />
                  Save and continue
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className='space-y-4'>
          <Alert>
            <Info size={14} strokeWidth={1.5} />
            <div>
              <AlertTitle>What happens next?</AlertTitle>
              <AlertDescription>
                After saving, you will be taken to the campaign page where you can add office locations, set FTE counts,
                and import an employee list.
              </AlertDescription>
            </div>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className='text-sm'>Checklist</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2 text-sm'>
              {[
                { done: true, label: 'Name the campaign' },
                { done: true, label: 'Pick reporting period' },
                { done: false, label: 'Add office locations & FTE' },
                { done: false, label: 'Import employee list' },
                { done: false, label: 'Send invitations' },
              ].map((item) => (
                <div key={item.label} className='flex items-center gap-2'>
                  <div
                    className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${
                      item.done ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                    }`}>
                    {item.done ? <span className='text-[10px] leading-none'>&#10003;</span> : null}
                  </div>
                  <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
};

const mapStateToProps = (state) => ({
  loading: state.survey?.loading || {},
  reportingPeriods: state.survey?.reportingPeriods || [],
});

const mapDispatchToProps = {
  getReportingPeriods: SURVEY_ACTIONS.getReportingPeriods,
  createCampaign: SURVEY_ACTIONS.createCampaign,
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveyCreate);
